/**
 * Link routing helpers.
 *
 * eesy.dk is served by two backends behind Azure Front Door:
 *  - the Next.js site (this app) on the default route `/*`
 *  - the legacy Mobile Partners backend for the order flow and a few `.asp` pages
 *
 * The paths below are reverse-proxied to Mobile Partners (mirror of
 * `patterns_to_match` in terraform/frontdoor.tf). They are NOT Next.js routes,
 * so they must be rendered as plain `<a>` (full-page navigation). Using
 * next/link would trigger an RSC prefetch (`?_rsc=`) that fails CORS once Front
 * Door redirects the request to the old.eesy.dk origin.
 *
 * Keep this list in sync with terraform/frontdoor.tf.
 *
 * Note: `/login` itself is a Next.js redirect (→ mit.eesy.dk), but is kept
 * here so that `/login/*` sub-paths (still proxied) render as plain `<a>`.
 */
export const PROXIED_PATHS = [
  '/bestil',
  '/application',
  '/cookie-information.asp',
  '/login',
  '/login.asp',
  '/smartwatch-oprettelse.asp',
  '/smartwatch-selvbetjening.asp',
] as const;

/** Hostnames that resolve to this same site (used to classify absolute URLs). */
const SITE_HOSTS = ['eesy.dk', 'www.eesy.dk'];

/** True when `pathname` matches one of the Mobile Partners proxied paths (exact or `/path/*`). */
export function isProxiedPath(pathname: string): boolean {
  return PROXIED_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

function hrefPathname(href: string): string | null {
  if (/^https?:\/\//i.test(href)) {
    try {
      return new URL(href).pathname;
    } catch {
      return null;
    }
  }

  if (href.startsWith('mailto:') || href.startsWith('tel:') || href.startsWith('#')) {
    return null;
  }

  const path = href.startsWith('/') ? href : `/${href}`;
  return path.split(/[?#]/)[0];
}

/**
 * Buy / basket links into the Mobile Partners order flow (`/bestil`).
 * These are full-page navigations that can take several seconds (basket create).
 */
export function isOrderFlowHref(href: string | undefined | null): boolean {
  if (!href) return false;
  const pathname = hrefPathname(href);
  return pathname === '/bestil' || Boolean(pathname?.startsWith('/bestil/'));
}

/**
 * Whether an href should be navigated with next/link (client-side routing).
 *
 * Returns false for proxied legacy paths, external hosts, and non-http schemes
 * (`mailto:`, `tel:`, `#…`) so the caller renders a plain `<a>` instead.
 */
export function isInternalRoute(href: string | undefined | null): boolean {
  if (!href) return false;

  if (/^https?:\/\//i.test(href)) {
    try {
      const url = new URL(href);
      if (!SITE_HOSTS.includes(url.hostname)) return false;
      return !isProxiedPath(url.pathname);
    } catch {
      return false;
    }
  }

  const pathname = hrefPathname(href);
  return pathname !== null && !isProxiedPath(pathname);
}

// ─── Dækningskort ────────────────────────────────────────────────────────────
// Vi har vores eget dækningskort på /daekningskort. Links i Contentful (fx footerens "Dækningskort") peger
// stadig på TDC's kort. De omskrives her, så de følger med deployet af siden i stedet for at skulle ændres i
// Contentful på samme tid som en deploy (Contentful går live med det samme og ville give et dødt link).
// Når linket i Contentful er rettet til /daekningskort, kan omskrivningen fjernes.
export const OWN_COVERAGE_PATH = '/daekningskort';
const TDC_COVERAGE_MAP_HREF = /^https?:\/\/daekning\.tdc\.dk\/tdcnetmap/i;

/** True for links til TDC's eget dækningskort (daekning.tdc.dk/tdcnetmap…). */
export function isTdcCoverageMapHref(href: string | undefined | null): boolean {
  return !!href && TDC_COVERAGE_MAP_HREF.test(href.trim());
}

/** Peger links til TDC's dækningskort om til vores eget. Alle andre links er uændrede. */
export function resolveCoverageHref(href: string): string {
  return isTdcCoverageMapHref(href) ? OWN_COVERAGE_PATH : href;
}

/** Som `resolveCoverageHref`, men slår også "åbn i nyt vindue" fra, da vores eget kort er på samme site. */
export function resolveContentfulLink<T extends { href: string; openInNewTab?: boolean }>(link: T): T {
  if (!isTdcCoverageMapHref(link.href)) return link;
  return { ...link, href: OWN_COVERAGE_PATH, openInNewTab: false };
}
